import { TitleCasePipe } from '@angular/common';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FilterOption, ListResponse, ListSearch, ModuleCategoryV1Read, ModuleComplianceV1Read, ModuleV1Read, SortOption } from '@ca/core/models';
import {
    ModuleCategoriesV1Service,
    ModuleCompliancesV1Service,
    ModulesPublicServiceV1,
    UserNotificationService,
    UserWorkspaceService,
} from '@ca/core/services';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { OptionItem } from 'src/app/core/models/option-item.model';
import { defaultPageNumber } from '../../constants/constants';

@Component({
    selector: 'ca-module-list-complex-remove',
    templateUrl: './module-list-complex-remove.component.html',
    styleUrls: ['./module-list-complex-remove.component.scss'],
})
export class ModuleListComplexRemoveComponent implements OnInit {
    @Input() availableFilterOptions: OptionItem<FilterOption>[];
    @Input() sortOptions: OptionItem<SortOption>[];
    @Input() showCreateButton: boolean = false;
    @Input() pageSize: number = 11;
    @Output() onModulesChange: EventEmitter<ModuleV1Read[]> = new EventEmitter();

    createButtonIdentifier: string = 'createButton';
    itemsPerColumn: number = 4;
    isLoading = false;
    modules: ModuleV1Read[] = [];
    modulesTotalCount: number = 0;
    categoryOptions: OptionItem<string>[];
    complianceOptions: OptionItem<string>[] = [];

    get gridRowCount(): number[] {
        // The number of rows depends on the amount of modules and items per column
        const rowCount = Math.ceil(this.modules.length / this.itemsPerColumn);
        // This array includes an array of numbers for the rows, e.g. "[0, 1]" for 2 rows
        return Array.from(Array(rowCount).keys());
    }

    get hasGitCredentials(): boolean {
        return this.userWorkspaceService.hasGitCredentials;
    }

    set hasGitCredentials(value: boolean) {
        this.userWorkspaceService.hasGitCredentials = value;
    }

    constructor(
        private moduleCategoriesV1Service: ModuleCategoriesV1Service,
        private moduleComplianceV1Service: ModuleCompliancesV1Service,
        private modulesPublicServiceV1: ModulesPublicServiceV1,
        private titleCasePipe: TitleCasePipe,
        private userNotificationService: UserNotificationService,
        private userWorkspaceService: UserWorkspaceService,
    ) {}

    ngOnInit(): void {
        this.isLoading = true;

        if (!this.showCreateButton) {
            this.fetchCategories$()
                .pipe(
                    tap(c => {
                        this.categoryOptions = c;
                    }),
                    catchError((err: HttpErrorResponse) => {
                        console.error('Failed fetching module categories', err);
                        const errorMessage: string = err?.message ? err?.message : JSON.stringify(err);
                        this.userNotificationService.showError(errorMessage || 'Failed fetching categories');
                        return EMPTY;
                    }),
                )
                .subscribe();
        }

        this.fetchCompliances$()
            .pipe(
                tap((c: OptionItem<string>[]) => {
                    this.complianceOptions = c;
                }),
                catchError((err: HttpErrorResponse) => {
                    console.error('Failed fetching module compliances', err);
                    const errorMessage = err.message ? err.message : JSON.stringify(err);
                    this.userNotificationService.showError(errorMessage || 'Failed fetching compliances');
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    onListSearch(listSearch: ListSearch): void {
        const sort = listSearch.sortBy ? listSearch.sortBy : this.sortOptions[0].value;
        const searchTerm = listSearch.searchQuery;
        const pageNumber = listSearch.pageNumber ? listSearch.pageNumber : defaultPageNumber;
        const categoryIds = listSearch.categoryIds ? listSearch.categoryIds : '';
        const complianceIds = listSearch.complianceIds ? listSearch.complianceIds : '';
        const isOpenSource = listSearch.isOpenSource ? listSearch.isOpenSource : undefined;
        const isFeatured = listSearch.isFeatured ? listSearch.isFeatured : undefined;
        const isCommunityContent = listSearch.isCommunityContent ? listSearch.isCommunityContent : undefined;

        let params = new HttpParams()
            .set('page[number]', pageNumber.toString())
            .set('page[size]', '12')
            .set('sort', `-isfeatured,${sort}`)
            .set('search', searchTerm);

        this.isLoading = true;

        if (categoryIds) {
            params = params.set('categoryIds', categoryIds);
        }

        if (complianceIds) {
            params = params.set('complianceIds', complianceIds);
        }

        if (isOpenSource) {
            params = params.set('isopensource', isOpenSource.toString());
        }

        if (isFeatured) {
            params = params.set('isfeatured', isFeatured.toString());
        }

        if (isCommunityContent) {
            params = params.set('isfeatured', 'false');
        }

        if (isFeatured && isCommunityContent) {
            this.modulesTotalCount = 0;
            this.modules = [];
            this.isLoading = false;
            this.userNotificationService.showError('ERROR_MESSAGES.SELECTED_FEATURED_AND_COMMUNITY');
            return;
        }

        this.modulesPublicServiceV1
            .getPublicModules(params)
            .pipe(
                tap((res: ListResponse<ModuleV1Read>) => {
                    this.modulesTotalCount = res?.meta?.totalRecordCount ?? 0;
                    this.modules = res.data;

                    // Emit only on first load to ensure that empty list view is shown correctly
                    if (searchTerm === '' && categoryIds === '' && complianceIds === '') {
                        this.onModulesChange.emit(this.modules);
                    }

                    this.isLoading = false;
                }),
                catchError((err: HttpErrorResponse) => {
                    console.error('Failed loading modules', err);

                    this.isLoading = false;
                    this.onModulesChange.emit(this.modules);

                    const errorMessage: string = err.message ? err.message : JSON.stringify(err);
                    this.userNotificationService.showError(errorMessage || 'Failed fetching modules');
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private fetchCategories$(): Observable<OptionItem<string>[]> {
        return this.moduleCategoriesV1Service.getCategories().pipe(
            map((categories: ModuleCategoryV1Read[]) => {
                return categories.map(c => {
                    // 421289: Removed numbers from categories.
                    // To add them back: ${this.titleCasePipe.transform(c.name)} (${c.numberOfPublicModules})
                    return {
                        label: `${this.titleCasePipe.transform(c.name)}`,
                        value: c.id,
                    };
                });
            }),
        );
    }

    private fetchCompliances$(): Observable<OptionItem<string>[]> {
        return this.moduleComplianceV1Service.getCompliances().pipe(
            map((compliances: ModuleComplianceV1Read[]) => {
                return compliances.map(c => {
                    return {
                        label: c.name,
                        value: c.id,
                    };
                });
            }),
        );
    }
}
